import React, { useState } from 'react';
import { Alert, Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';

export interface NotebookReading {
  id: string;
  voltage: number;
  current: number;
  resistance: number;
  lengthMetres?: number;
  observation: string;
  timestamp: string;
}

interface NotebookProps {
  readings: NotebookReading[];
  onReadingsChange: (readings: NotebookReading[]) => void;
  onAutoRecord: () => Omit<NotebookReading, 'id' | 'timestamp' | 'observation'> | null;
  observations: string;
  onObservationsChange: (value: string) => void;
  onGenerateReport: () => void;
}

const Notebook: React.FC<NotebookProps> = ({ readings, onReadingsChange, onAutoRecord, observations, onObservationsChange, onGenerateReport }) => {
  const [message, setMessage] = useState('');
  const addManualRow = () => {
    const row: NotebookReading = { id: `manual-${Date.now()}`, voltage: 0, current: 0, resistance: 0, observation: '', timestamp: new Date().toISOString() };
    onReadingsChange([...readings, row]);
  };
  const autoRecord = () => {
    const measured = onAutoRecord();
    if (!measured) { setMessage('Close the switch and check the circuit before recording.'); return; }
    onReadingsChange([...readings, { ...measured, id: `reading-${Date.now()}`, observation: '', timestamp: new Date().toISOString() }]);
    setMessage('Meter readings added to the notebook.');
  };
  const updateRow = (id: string, patch: Partial<NotebookReading>) => onReadingsChange(readings.map((row) => row.id === id ? { ...row, ...patch } : row));
  const exportCsv = () => {
    const header = ['S.No.', 'Voltage (V)', 'Current (A)', 'Resistance (ohm)', 'Wire length (m)', 'Observations', 'Timestamp'];
    const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = readings.map((row, index) => [index + 1, row.voltage, row.current, row.resistance, row.lengthMetres ?? '', row.observation, row.timestamp].map(escape).join(','));
    const blob = new Blob([[header.map(escape).join(','), ...rows].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'ohms-law-readings.csv'; anchor.click(); URL.revokeObjectURL(url);
  };
  const exportPdf = () => {
    const pdf = new jsPDF();
    pdf.setFontSize(18); pdf.text("Ohm's Law — Laboratory Notebook", 14, 18);
    pdf.setFontSize(10); pdf.text(`Generated: ${new Date().toLocaleString()}`, 14, 26);
    let y = 38;
    pdf.setFont('helvetica', 'bold'); pdf.text('No.    Voltage (V)   Current (A)   Resistance (Ω)   Observation', 14, y); y += 7;
    pdf.setFont('helvetica', 'normal');
    readings.forEach((row, index) => {
      if (y > 275) { pdf.addPage(); y = 18; }
      pdf.text(`${index + 1}.       ${row.voltage.toFixed(3)}             ${row.current.toFixed(4)}              ${row.resistance.toFixed(3)}              ${row.observation.slice(0, 35)}`, 14, y);
      y += 7;
    });
    pdf.save('ohms-law-notebook.pdf');
  };

  return (
    <Paper className="ohm-panel" elevation={0}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: 1.5 }}>
        <Box><Typography variant="h6" fontWeight={800}>Laboratory notebook</Typography><Typography variant="body2" color="text.secondary">Read, record, compare, and explain your observations.</Typography></Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={autoRecord}>Record meters</Button>
          <Button size="small" variant="outlined" onClick={addManualRow}>Manual row</Button>
        </Stack>
      </Stack>
      {message && <Alert severity="info" onClose={() => setMessage('')} sx={{ mb: 1 }}>{message}</Alert>}
      <TableContainer className="ohm-notebook-table">
        <Table size="small" aria-label="Ohm's law readings">
          <TableHead><TableRow><TableCell>No.</TableCell><TableCell>V (V)</TableCell><TableCell>I (A)</TableCell><TableCell>R = V/I (Ω)</TableCell><TableCell>Observations</TableCell><TableCell aria-label="Actions" /></TableRow></TableHead>
          <TableBody>
            {readings.map((row, index) => <TableRow key={row.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell><TextField size="small" type="number" inputProps={{ step: '0.01', 'aria-label': `Voltage for reading ${index + 1}` }} value={row.voltage} onChange={(event) => { const voltage = Number(event.target.value); updateRow(row.id, { voltage, resistance: row.current ? voltage / row.current : 0 }); }} /></TableCell>
              <TableCell><TextField size="small" type="number" inputProps={{ step: '0.001', 'aria-label': `Current for reading ${index + 1}` }} value={row.current} onChange={(event) => { const current = Number(event.target.value); updateRow(row.id, { current, resistance: current ? row.voltage / current : 0 }); }} /></TableCell>
              <TableCell>{row.resistance > 0 ? row.resistance.toFixed(3) : '—'}</TableCell>
              <TableCell><TextField size="small" inputProps={{ 'aria-label': `Observation for reading ${index + 1}` }} value={row.observation} placeholder="Add observation" onChange={(event) => updateRow(row.id, { observation: event.target.value })} /></TableCell>
              <TableCell><IconButton size="small" aria-label={`Delete reading ${index + 1}`} onClick={() => onReadingsChange(readings.filter((item) => item.id !== row.id))}><DeleteOutlineIcon fontSize="small" /></IconButton></TableCell>
            </TableRow>)}
            {!readings.length && <TableRow><TableCell colSpan={6} align="center">No readings yet. Close the key and record the meters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <TextField fullWidth multiline minRows={3} label="Observations and conclusion" value={observations} onChange={(event) => onObservationsChange(event.target.value)} sx={{ mt: 2 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>Export CSV</Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportPdf}>Export PDF</Button>
        <Button size="small" variant="outlined" onClick={onGenerateReport}>Generate lab report</Button>
      </Box>
    </Paper>
  );
};

export default Notebook;
